import {Routes as DomRoutes, Route as DomRoute, matchPath, useLocation} from 'react-router-dom';
import React, {Suspense} from 'react';
import NotFoundPage from '../pages/NotFoundPage';
import routes from './routes.json';
import {localStorageKeysConstants} from '../constants';
import {StyledSpin} from '../app/StyledApp';
import {locationHelpers} from '../utils/helpers';
import {
  useHasObAccounts,
  useIsAccountingAvailable,
  useIsAdmin,
  useIsAuthenticated,
  useIsBudgetsAvailable,
  useIsExpensesDisabled,
  useIsManager,
  useIsPsd2ObAvailable,
  useIsRouteAccessResolved,
  useIsSubscriptionsDisabled,
  useIsTreezorEnabled,
  useIsVendorsDisabled
} from '../hooks';


// Route pages are loaded on demand. Every one of these is reachable only by navigating to its
// route, so shipping all of them in the entry bundle made every user download every screen they
// would never open (H1MVP-4741).
//
// NotFoundPage deliberately stays eager above: it is the `*` element, it appears in BOTH branches
// of the catch-all ternary below, and six other pages import it directly — so it ends up in a
// shared chunk regardless, and a suspense fallback on a 404 would replace the intentional
// access-loading spinner that the ternary exists to show.
// A route's chunk cannot start downloading until the authenticated route table below exists, and
// that table does not exist until `users/current` resolves — so the connection sits idle for the
// whole auth round trip and then fetches dozens of chunks at once. Registering each gated page's
// loader against its route key lets the app start the matching one at boot instead (H1MVP-4782).
//
// Public pages are deliberately NOT registered: they are already in the route table at t=0, so
// their code is never waiting on auth and there is nothing to bring forward.
const gatedChunkLoaders = {};

const gatedPage = (routeKey, loader) => {
  gatedChunkLoaders[routeKey] = loader;
  return React.lazy(loader);
};

const findGatedRouteKey = (pathname) => Object.keys(gatedChunkLoaders)
  .find(key => routes[key] && matchPath({path: routes[key], end: true}, pathname));

/**
 * Starts loading the code for `pathname` without waiting for the route table to admit it.
 *
 * Safe to call more than once and safe to ignore: the loader is the same function `React.lazy`
 * holds, so the module registry de-duplicates and the route mounts against an already-resolved
 * module. A rejection here is not surfaced — the route's own Suspense boundary will retry the
 * import and report the failure in the one place that can render it.
 */
export const prefetchRouteChunk = (pathname) => {
  if (!localStorage.getItem(localStorageKeysConstants.TOKEN)) return;

  gatedChunkLoaders[findGatedRouteKey(pathname)]?.().catch(() => {});
};

const CardPage = gatedPage('cardDetails', () => import('../pages/CardPage'));
const CardsPage = gatedPage('cardsList', () => import('../pages/CardsPage'));
const OverviewRouterPage = gatedPage('overview', () => import('../pages/Overview/OverviewRouterPage'));
const TeamUserPage = gatedPage('teamUserDetails', () => import('../pages/TeamUserPage'));
const SettingsPage = gatedPage('settings', () => import('../pages/SettingsPage'));
const ProfileSettingsPage = gatedPage('profile', () => import('../pages/ProfileSettingsPage'));
const SignUpPage = React.lazy(() => import('../pages/SignUpPage'));
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const ForgotPinPage = React.lazy(() => import('../pages/ForgotPinPage'));
const TransactionsPage = gatedPage('transactionsList', () => import('../pages/TransactionsPage'));
const SubscriptionsPage = gatedPage('subscriptionsList', () => import('../pages/SubscriptionsPage'));
const SubscriptionPage = gatedPage('subscriptionDetails', () => import('../pages/SubscriptionPage'));
const CompanyInvitePage = React.lazy(() => import('../pages/CompanyInvitePage'));
const ExportDownloadPage = React.lazy(() => import('../pages/ExportDownloadPage'));
const EmailVerificationPage = React.lazy(() => import('../pages/EmailVerificationPage'));
const VerifySuccessPage = React.lazy(() => import('../pages/VerifySuccessPage'));
const UserInvitePage = React.lazy(() => import('../pages/UserInvitePage'));
const OAuthCallbackPage = React.lazy(() => import('../pages/OAuthCallbackPage'));
const ConnectReturnPage = React.lazy(() => import('../pages/ConnectReturnPage/ConnectReturnPage'));
const QuickGoogleConnectPage = React.lazy(() => import('../pages/QuickGoogleConnectPage'));
const PublicSubscriptionUpdatePage = React.lazy(() => import('../pages/PublicSubscriptionUpdatePage'));
const ExpensesPage = gatedPage('expensesList', () => import('../pages/ExpensesPage'));
const InvoicePage = gatedPage('invoiceDetails', () => import('../pages/InvoicePage'));
const InvoicesPage = gatedPage('invoicesList', () => import('../pages/InvoicesPage'));
const NetSuiteBillsPage = gatedPage('netSuiteVendorBills', () => import('../pages/NetSuiteBillsPage'));
const NetSuiteJournalEntriesPage = gatedPage('netSuiteJournalEntriesList', () => import('../pages/NetSuiteJournalEntriesPage'));
const NetSuiteVendorsPage = gatedPage('netSuiteVendors', () => import('../pages/NetSuiteVendorsPage'));
const SupportPage = gatedPage('support', () => import('../pages/SupportPage'));
const VendorsPage = gatedPage('vendorsList', () => import('../pages/VendorsPage'));
const BudgetsActivePage = gatedPage('budgetsActive', () => import('../pages/BudgetsActivePage'));
const BudgetsRequestsPage = gatedPage('budgetsRequests', () => import('../pages/BudgetsRequestsPage'));
const BudgetActiveDetailsPage = gatedPage('budgetActiveDetails', () => import('../pages/BudgetActiveDetailsPage'));
const BudgetRequestDetailsPage = gatedPage('budgetRequestsDetails', () => import('../pages/BudgetRequestDetailsPage'));

/**
 * Shown while a route's chunk is in flight.
 *
 * `.route-suspense-fallback` is here for the automated route check, which asserts the boundary has
 * RESOLVED — a fallback that never goes away reads as a slow page rather than a broken one, and
 * would otherwise survive a visual pass.
 */
const RouteFallback = () => (
  <StyledSpin
    className='route-suspense-fallback'
    spinning={true}
    wrapperClassName='full-width-spin'
  />
);

const Routes = () => {
  const location = useLocation();

  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const isAuthenticated = useIsAuthenticated();
  const hasObAccounts = useHasObAccounts();
  const isRouteAccessResolved = useIsRouteAccessResolved();

  const isAccountingAvailable = useIsAccountingAvailable();
  const isBudgetsAvailable = useIsBudgetsAvailable();
  const isPsd2ObAvailable = useIsPsd2ObAvailable();

  const isTreezorEnabled = useIsTreezorEnabled();

  const isTransactionsAvailable = isTreezorEnabled || isPsd2ObAvailable;

  const isExpensesDisabled = useIsExpensesDisabled();
  const isSubscriptionsDisabled = useIsSubscriptionsDisabled();
  const isVendorsDisabled = useIsVendorsDisabled();

  // Paths of modules switched off for the company. Their routes are not registered, so they
  // must resolve to 404 rather than the access-loading spinner below.
  // `subscriptionsList` ('/subscriptions') also covers the details path via prefix matching.
  const disabledModulePaths = [
    isExpensesDisabled && routes.expensesList,
    isSubscriptionsDisabled && routes.subscriptionsList,
    isVendorsDisabled && routes.vendorsList
  ].filter(Boolean);

  const Route = (key, element) => {
    if (routes.hasOwnProperty(key)) {
      return (
        <DomRoute
          key={key}
          exact
          path={routes[key]}
          element={element}
        />
      )
    } else {
      return null;
    }
  };

  const getAdminRoutes = () => {
    return [
      Route('settings', <SettingsPage />),
      Route('teamUserDetails', <TeamUserPage />),
    ];
  }

  const getMainRoutes = () => {
    const isGatedRoute = Boolean(findGatedRouteKey(location.pathname));
    const isDisabledModuleRoute = locationHelpers.isActivePathInList(location.pathname, disabledModulePaths);
    // Gated routes are only registered once the flags and roles they depend on have arrived, so a
    // direct link to an available page lands here first. Hold a loader until access is known; only
    // then is an unregistered path really a 404. A route disabled by a company feature is
    // definitive — never a spinner.
    const isAwaitingRouteAccess = isAuthenticated && isGatedRoute && !isDisabledModuleRoute && !isRouteAccessResolved;
    return [
      <DomRoute
        element={<CompanyInvitePage isAuth={isAuthenticated} />}
        key='invite'
        exact
        path={routes.invite}
      />,
      // Reachable whether or not the caller is logged in -- the emailed export link must
      // not 404 before login (plan ready-to-use-accounting-exports.md §5 story 3b).
      <DomRoute
        element={<ExportDownloadPage isAuthenticated={isAuthenticated} />}
        key='exportDownload'
        exact
        path={routes.exportDownload}
      />,
      Route('verification', <EmailVerificationPage />),
      Route('verifyIdentity', <VerifySuccessPage />),
      Route('userInvite', <UserInvitePage />),
      Route('connectReturn', <ConnectReturnPage />),
      Route('oauthCallback', <OAuthCallbackPage />),
      Route('publicSubscriptionUpdate', <PublicSubscriptionUpdatePage />),
      Route('quickGoogleConnect', <QuickGoogleConnectPage />),
      <DomRoute
        element={isAwaitingRouteAccess ? <RouteFallback /> : <NotFoundPage />}
        path='*'
        key='not-found'
      />
    ];
  }

  const adminRoutes = getAdminRoutes();

  const mainRoutes = getMainRoutes();

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <DomRoutes>
          {Route('signUp', <SignUpPage />)}
          {Route('login', <LoginPage />)}
          {Route('forgotPin', <ForgotPinPage />)}
          {mainRoutes}
        </DomRoutes>
      </Suspense>
    )
  }

  const getAppRoutes = () => {
    let appRoutes = [
      Route('overview', <OverviewRouterPage />),
      !isExpensesDisabled && Route('expensesList', <ExpensesPage />),
      !isSubscriptionsDisabled && Route('subscriptionDetails', <SubscriptionPage />),
      Route('profile', <ProfileSettingsPage />),
      Route('support', <SupportPage />),
    ];

    if (isTreezorEnabled) {
      appRoutes = [ ...appRoutes, Route('cardDetails', <CardPage />) ];

      if (isAdmin || isManager) {
        appRoutes = [
          ...appRoutes,
          Route('cardsList', <CardsPage />),
        ];
      }
    }

    if ((isTransactionsAvailable && isAdmin) || (isPsd2ObAvailable && !isAdmin && hasObAccounts)) {
      appRoutes = [
        ...appRoutes,
        Route('transactionsList', <TransactionsPage />)
      ];
    }

    if (isBudgetsAvailable) {
      appRoutes = [
        ...appRoutes,
        Route('budgetsActive', <BudgetsActivePage />),
        Route('budgetsRequests', <BudgetsRequestsPage />),
        Route('budgetActiveDetails', <BudgetActiveDetailsPage />),
        Route('budgetRequestsDetails', <BudgetRequestDetailsPage />),
      ];
    }

    if (isAdmin || isManager) {
      appRoutes = [
        ...appRoutes,
        !isSubscriptionsDisabled && Route('subscriptionsList', <SubscriptionsPage />),
        Route('invoiceDetails', <InvoicePage />),
        Route('invoicesList', <InvoicesPage />),
      ];
    }

    if (isAdmin) {
      appRoutes = [
        ...appRoutes,
        ...adminRoutes,
        !isVendorsDisabled && Route('vendorsList', <VendorsPage />),
      ];
      if (isAccountingAvailable) {
        appRoutes = [
          ...appRoutes,
          Route('netSuiteVendorBills', <NetSuiteBillsPage />),
          Route('netSuiteVendors', <NetSuiteVendorsPage />),
          Route('netSuiteJournalEntriesList', <NetSuiteJournalEntriesPage />),
        ];
      }
    }

    appRoutes = [...appRoutes, ...mainRoutes];

    return appRoutes;
  }

  const appRoutes = getAppRoutes();

  return (
    <Suspense fallback={<RouteFallback />}>
      <DomRoutes location={location} key={location.pathname}>
        {appRoutes}
      </DomRoutes>
    </Suspense>
  );
}

export default Routes;
